import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { NasMark } from "@/components/brand/NasLogo";
import { api } from "@/lib/api";
import { downloadDeckPdf } from "@/lib/deckPdf";
import { RequireAdmin } from "@/context/AdminAuth";
import "./headliner.css";

/* Felles viewer/print-skall for slide-native 16:9-deck (1920×1080 canvas, skalert til viewport).
   Mottakerlag: admin ?for=<mottaker>&category=<kategori>; offentlig KUN via anonymt token ?r=… (løses opp i DeckGate → RecipientCtx),
   så ingen kan bytte navn i lenken. PDF: bakgrunnsjobb via /api/deck/<deck>/pdf/jobs. */

const W = 1920, H = 1080;
const PAD_X = 28, PAD_Y = 18; /* optisk balanse: litt mer luft på sidene enn oppe/nede; 0 i fullskjerm */
const MH = 44;                /* mobil: høyde på toppstripen over sliden */

/* Visningsmodus: desktop (sentrert m/ luft) · portrait (mobil: kant-til-kant øverst, verktøy under) · landscape (mobil: fyll skjermen) */
const layoutOf = () => {
  const w = window.innerWidth, h = window.innerHeight;
  const full = !!document.fullscreenElement;
  const touchSmall = Math.min(w, h) <= 820 && window.matchMedia?.("(hover: none)").matches;
  if (touchSmall && h > w) return { s: w / W, fs: full, mode: "portrait" };
  if (touchSmall) return { s: Math.min(w / W, h / H), fs: full, mode: "landscape" };
  const px = full ? 0 : PAD_X, py = full ? 0 : PAD_Y;
  return { s: Math.min((w - px * 2) / W, (h - py * 2) / H), fs: full, mode: "desktop" };
};

export const useViewer = () => {
  const [lay, setLay] = useState(() => (typeof window === "undefined" ? { s: 1, fs: false, mode: "desktop" } : layoutOf()));
  useLayoutEffect(() => {
    const m = () => setLay(layoutOf());
    m();
    window.addEventListener("resize", m);
    window.addEventListener("orientationchange", m);
    document.addEventListener("fullscreenchange", m);
    return () => {
      window.removeEventListener("resize", m);
      window.removeEventListener("orientationchange", m);
      document.removeEventListener("fullscreenchange", m);
    };
  }, []);
  return [lay.s, lay.fs, lay.mode];
};

/* Merket: NAS-mark («the shift») + wordmark — brukes i alle masthead/lockups/nav i dekkene */
export const Brand = ({ size = 15, className = "b" }) => (
  <span className={`${className} hd-brandmark`} data-testid="hd-brandmark"><NasMark size={size} /><span>Norwegian AI Summit</span></span>
);

/* Publikasjonshode: wordmark over dokumenttype – to nivåer, ingen breadcrumb */
export const Nav = ({ doc }) => (
  <span className="hd-nav" data-testid="hd-wordmark">
    <Brand />
    <span className="d">{doc}</span>
  </span>
);

/* Felles ramme for innholdsslides – nr kommer fra rekkefølgen i SLIDES */
/* bare: kun sidetall — for statements som skal stå alene på papiret */
export const Frame = ({ nr, label, mottaker, className, bare = false, prep = "Prepared for", children }) => (
  <section className={`hd-slide ${className}`} data-testid={`hd-slide-${nr}`}>
    {!bare && (
      <header className="hd-top hd-frame">
        <Nav doc={label} />
      </header>
    )}
    {children}
    <footer className="hd-bottom hd-frame">
      <span className="hd-nr">{nr}</span>
      {!bare && mottaker && <span className="hd-prep">{prep} {mottaker}</span>}
    </footer>
  </section>
);

export const nrOf = (i) => String(i + 1).padStart(2, "0");

/* Illustrativt AI-motiv: et felt av punkter der ett tyngdepunkt trer fram av støyen («mange punkter, ett rom»).
   Deterministisk (identisk i PDF), monokromt (currentColor), myke kanter (ingen rektangel). Aldri robot/hjerne/kretskort. */
export const Field = ({ cols = 40, rows = 20, gap = 24, focus = [0.8, 0.45], spread = 0.5, rmin = 0.6, rmax = 3.6, className = "", testid }) => {
  const w = (cols - 1) * gap, h = (rows - 1) * gap;
  const smooth = (v) => (v >= 1 ? 1 : v <= 0 ? 0 : v * v * (3 - 2 * v));
  const dots = [];
  for (let i = 0; i < cols; i++) {
    for (let j = 0; j < rows; j++) {
      const x = i / (cols - 1), y = j / (rows - 1);
      const dx = x - focus[0], dy = (y - focus[1]) * (h / w);
      const t = Math.exp(-((dx * dx + dy * dy) / (spread * spread)) * 2.2);
      const edge = smooth(Math.min(x, 1 - x) / 0.24) * smooth(Math.min(y, 1 - y) / 0.3);
      const o = (0.2 + 0.8 * t) * edge;
      if (o < 0.03) continue;
      dots.push(<circle key={`${i}-${j}`} cx={i * gap} cy={j * gap} r={((rmin + (rmax - rmin) * t) * (0.55 + 0.45 * edge)).toFixed(2)} opacity={o.toFixed(2)} />);
    }
  }
  return (
    <svg className={`hd-field ${className}`} viewBox={`-4 -4 ${w + 8} ${h + 8}`} width={w + 8} height={h + 8} fill="currentColor" aria-hidden data-testid={testid}>
      {dots}
    </svg>
  );
};

const RecipientCtx = createContext(null);

export const useMottaker = (fallback) => {
  const [sp, setSp] = useSearchParams();
  const rcp = useContext(RecipientCtx);
  const mottaker = (rcp ? rcp.mottaker : (sp.get("for") || "").trim()) || fallback;
  const category = rcp ? rcp.kategori : (sp.get("category") || "").trim();
  const sett = (key, val) => {
    const n = new URLSearchParams(sp);
    if (val.trim()) n.set(key, val.trim()); else n.delete(key);
    setSp(n, { replace: true });
  };
  return { sp, mottaker, category, token: rcp?.token || "", sett };
};

/* Print-modus: alle slides stablet som 16:9-sider (brukes av PDF-eksporten via Chromium) */
export function DeckPrint({ slides, fallback, theme = "dark", venue = "strong", rootStyle }) {
  const { mottaker, category } = useMottaker(fallback);
  const alle = typeof slides === "function" ? slides(mottaker, category) : slides;
  const only = parseInt(new URLSearchParams(window.location.search).get("only") || "0", 10);
  const list = only > 0 ? alle.slice(0, only) : alle;
  const [klar, setKlar] = useState(false);
  useEffect(() => {
    const bilder = Array.from(document.images);
    Promise.all(bilder.map((b) => (b.complete ? null : new Promise((r) => { b.onload = r; b.onerror = r; })))).then(() => setKlar(true));
  }, []);
  return (
    <div className="hd-root hd-print" data-font="geist" data-theme={theme} data-venue={venue} style={rootStyle?.(mottaker)} data-testid="hd-print">
      {klar && <span data-testid="hd-print-ready" hidden />}
      {list.map((Slide, i) => (
        <div className="hd-page" key={i}>
          <div className="hd-stage"><Slide nr={nrOf(i)} mottaker={mottaker} category={category} theme={theme} venue={venue} print /></div>
        </div>
      ))}
    </div>
  );
}

const lastNedPdf = async (deck, mottaker, category, prefix, setBusy, token) => {
  setBusy(0);
  try {
    await downloadDeckPdf({ deck, mottaker, kategori: category, token, filePrefix: prefix, onProgress: setBusy });
  } catch (e) {
    window.alert(e.message);
  } finally {
    setBusy(null);
  }
};

/* Bildecache uavhengig av HTTP-headere (preview-proxyen sender no-store): hvert bilde hentes én gang og
   serveres som blob-URL til alle senere <Img>. Slide-bytter venter da aldri på nettverk. */
const AssetCtx = createContext(false);
const blobs = new Map();
const usePreload = (assets) => {
  const [, tick] = useState(0);
  useEffect(() => {
    let live = true;
    const t = setTimeout(() => {
      assets.forEach(async (u) => {
        if (blobs.has(u)) return;
        blobs.set(u, null);
        try {
          const b = await (await fetch(u)).blob();
          blobs.set(u, URL.createObjectURL(b));
          if (live) tick((n) => n + 1);
        } catch {
          blobs.delete(u);
        }
      });
    }, 300);
    return () => { live = false; clearTimeout(t); };
  }, [assets]);
};

/* <img> som bruker blob-cachen når den finnes ved montering (kilden byttes ikke midt i en animasjon) */
export const Img = ({ src, ...rest }) => {
  const on = useContext(AssetCtx);
  const [kilde] = useState(() => (on && blobs.get(src)) || src);
  return <img src={kilde} {...rest} />;
};

/* Inngangsanimasjonene holdes til slidens bilder er *dekodet* og fonten er klar (maks 2,5 s) — ingen «popping» av foto/font midt i en wipe */
const useSlideReady = (stageRef, i) => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    setReady(false);
    let live = true;
    const imgs = Array.from(stageRef.current?.querySelectorAll("img") || []);
    const waits = imgs.map((im) => (im.decode ? im.decode().catch(() => {}) : Promise.resolve()));
    if (document.fonts?.ready) waits.push(document.fonts.ready.catch(() => {}));
    const t = setTimeout(() => live && setReady(true), 2500);
    Promise.all(waits).then(() => live && setReady(true));
    return () => { live = false; clearTimeout(t); };
  }, [stageRef, i]);
  return ready;
};

export function DeckViewer({ deck, slides: slidesProp, fallback, filePrefix, assets = [], theme: themeDefault = "dark", venue: venueDefault = "strong", categoryField = true, publicMode = false, rootStyle, no = false }) {
  const en = publicMode && !no;
  const [s, fs, mode] = useViewer();
  const [busy, setBusy] = useState(null);
  const [rot, setRot] = useState(false);
  const stageRef = useRef(null);
  const touch = useRef(null);
  usePreload(assets);
  const { sp, mottaker, category, token, sett } = useMottaker(fallback);
  const slides = typeof slidesProp === "function" ? slidesProp(mottaker, category) : slidesProp;
  const start = Math.min(Math.max(parseInt(sp.get("slide") || "1", 10) - 1, 0), slides.length - 1);
  const [i, setI] = useState(start);
  const font = sp.get("font") || "geist";
  const theme = sp.get("theme") || themeDefault;
  const venue = sp.get("venue") || venueDefault;
  const n = slides.length;
  const neste = () => setI((v) => Math.min(v + 1, n - 1));
  const forrige = () => setI((v) => Math.max(v - 1, 0));

  useEffect(() => {
    const k = (e) => {
      if (e.target.tagName === "INPUT") return;
      if (e.key === "ArrowRight" || e.key === "PageDown" || e.key === " ") setI((v) => Math.min(v + 1, slides.length - 1));
      if (e.key === "ArrowLeft" || e.key === "PageUp") setI((v) => Math.max(v - 1, 0));
      if (e.key === "f" || e.key === "F") {
        if (document.fullscreenElement) document.exitFullscreen();
        else document.documentElement.requestFullscreen?.();
      }
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [slides.length]);

  const mobile = mode === "portrait";
  const rotert = mobile && rot;
  const klikk = (e) => {
    if (rotert) { setI((v) => (e.clientY < window.innerHeight / 2 ? Math.max(v - 1, 0) : Math.min(v + 1, n - 1))); return; }
    const midt = window.innerWidth / 2;
    setI((v) => (e.clientX < midt ? Math.max(v - 1, 0) : Math.min(v + 1, n - 1)));
  };
  /* Sveip: horisontalt i vanlig visning, vertikalt når sliden er rotert (opp = neste) */
  const touchStart = (e) => { touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; };
  const touchEnd = (e) => {
    const t = touch.current;
    touch.current = null;
    if (!t) return;
    const dx = e.changedTouches[0].clientX - t.x, dy = e.changedTouches[0].clientY - t.y;
    const d = rotert ? -dy : dx;
    if (Math.abs(d) > 40 && Math.abs(d) > Math.abs(rotert ? dx : dy)) { d < 0 ? neste() : forrige(); e.preventDefault(); }
  };

  /* Stagen snappes til hele piksler (bredde i 16-multipler → høyde også heltall) og posisjoneres med heltalls left/top.
     Brøkdels-kanter ga en 1 px «søm» langs sidene mens cover-fotoet/Kevin animerte (komposittlag vs. skyggelaget) */
  const sEff = rotert ? Math.min(window.innerHeight / W, window.innerWidth / H) : s;
  const snapW = Math.max(16, Math.floor((W * s) / 16) * 16), snapS = snapW / W, snapH = (snapW * 9) / 16;
  const left = `${Math.round((window.innerWidth - snapW) / 2)}px`;
  const stageStyle = rotert
    ? { top: "50%", transform: `translate(-50%, -50%) rotate(90deg) scale(${sEff})` }
    : mobile
      ? { left, top: `${MH}px`, transformOrigin: "0 0", transform: `scale(${snapS})` }
      : { left, top: `${Math.round((window.innerHeight - snapH) / 2)}px`, transformOrigin: "0 0", transform: `scale(${snapS})` };

  const ready = useSlideReady(stageRef, i);
  const Slide = slides[Math.min(i, slides.length - 1)];
  return (
    <div className="hd-root" data-font={font} data-theme={theme} data-venue={venue} data-fs={fs ? "1" : "0"} data-ready={ready ? "1" : "0"} data-mobile={mobile ? (rotert ? "rot" : "1") : mode === "landscape" ? "land" : "0"}
      style={rootStyle?.(mottaker)} data-testid="hd-root" onClick={klikk} onTouchStart={touchStart} onTouchEnd={touchEnd}>
      {mobile && !rotert && (
        <div className="hd-mhead" onClick={(e) => e.stopPropagation()} data-testid="hd-mhead"><Brand size={11} className="hd-mhead-b" /><span>{no ? "Fortrolig" : <>Private &amp; confidential</>}</span></div>
      )}
      <div className="hd-stage" ref={stageRef} style={stageStyle} data-testid="hd-stage">
        <AssetCtx.Provider value>
          <Slide key={i} nr={nrOf(i)} mottaker={mottaker} category={category} theme={theme} venue={venue} />
        </AssetCtx.Provider>
      </div>
      {rotert && (
        <button type="button" className="hd-mclose" onClick={(e) => { e.stopPropagation(); setRot(false); }} aria-label="Exit rotated view" data-testid="hd-rot-exit">×</button>
      )}
      {mobile && !rotert && (
        <div className="hd-mtools" style={{ top: `${MH + H * s + 14}px` }} onClick={(e) => e.stopPropagation()} data-testid="hd-mtools">
          <div className="hd-mnav">
            <button type="button" onClick={forrige} disabled={i === 0} aria-label="Previous slide" data-testid="hd-m-prev">←</button>
            <span className="hd-tools-nr" data-testid="hd-tools-nr">{nrOf(i)} / {nrOf(n - 1)}</span>
            <button type="button" onClick={neste} disabled={i === n - 1} aria-label="Next slide" data-testid="hd-m-next">→</button>
          </div>
          <div className="hd-mactions">
            <button type="button" className="hd-mbtn" onClick={() => lastNedPdf(deck, mottaker, category, filePrefix, setBusy, token)} disabled={busy !== null} data-testid="hd-pdf-btn">
              {busy !== null ? `${en ? "Preparing PDF" : "Lager PDF"} · ${busy}%` : en ? "Download PDF" : "Last ned PDF"}
            </button>
            <button type="button" className="hd-mbtn" onClick={() => setRot(true)} data-testid="hd-rot-btn">{en ? "Rotate view" : "Roter visning"}</button>
          </div>
          <p className="hd-mhint">{en ? "Swipe to navigate · turn your phone for the full-screen view" : "Sveip for å bla · snu telefonen for fullskjerm"}</p>
        </div>
      )}
      {!fs && !mobile && (
        <div className="hd-tools" onClick={(e) => e.stopPropagation()}>
          {!publicMode && (
            <label className="hd-tool-field">
              <span>Prepared for</span>
              <input className="hd-tool-in" defaultValue={sp.get("for") || ""} placeholder={fallback || "Recipient"} onBlur={(e) => sett("for", e.target.value)} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} data-testid="hd-for-input" />
            </label>
          )}
          {!publicMode && categoryField && (
            <label className="hd-tool-field">
              <span>{typeof categoryField === "string" ? categoryField : "Category"}</span>
              <input className="hd-tool-in" defaultValue={category} placeholder={typeof categoryField === "string" ? "speaker · internal" : "banking"} onBlur={(e) => sett("category", e.target.value)} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} data-testid="hd-category-input" />
            </label>
          )}
          <span className="hd-tools-nr" data-testid="hd-tools-nr">{nrOf(i)} / {nrOf(slides.length - 1)}</span>
          <button type="button" className="hd-tool" onClick={() => lastNedPdf(deck, mottaker, category, filePrefix, setBusy, token)} disabled={busy !== null} data-testid="hd-pdf-btn">
            {busy !== null ? `${en ? "Preparing PDF" : "Lager PDF"} · ${busy}%` : en ? "Download PDF" : "Last ned PDF"}
          </button>
          <button type="button" className="hd-tool" onClick={() => document.documentElement.requestFullscreen?.()} data-testid="hd-fs-btn">{en ? "Fullscreen" : "Fullskjerm"}</button>
        </div>
      )}
    </div>
  );
}

/* Print-ruten: engangs render-kode fra PDF-eksporten (Chromium, ingen cookies) — ellers vanlig admin-krav */
export function RequirePrint({ children }) {
  const [sp] = useSearchParams();
  const rc = sp.get("rc");
  const [ok, setOk] = useState(rc ? null : "admin");
  useEffect(() => {
    if (!rc) return;
    api.get(`/deck/render/${rc}`).then(() => setOk("rc")).catch(() => setOk("no"));
  }, [rc]);
  if (ok === "admin") return <RequireAdmin>{children}</RequireAdmin>;
  if (ok === "rc") return children;
  if (ok === "no") return <div data-testid="hd-render-denied" style={{ padding: 40, fontFamily: "sans-serif" }}>Render code invalid or expired.</div>;
  return null;
}

/* Offentlig visning med felles passord: token → status → gate → deck. Cookie (nais_deck) varer 30 dager.
   Mottakeren kommer kun fra det anonyme tokenet (?r=), aldri fra ?for=. Status-kallet sender tokenet, så PDF-en produseres i
   bakgrunnen mens mottakeren leser. requireRecipient: decket finnes ikke generisk (Build) — uten gyldig token vises en beskjed. */
const GATE_EN = { h: "This deck is private.", p: "Enter the password you received to open it.", ph: "Password", btn: "Open", busy: "Opening…", foot: <>Private &amp; confidential · 13 April 2027 · Grieghallen, Bergen</>,
  e429: "Too many attempts. Try again in 15 minutes.", e401: "That password is not right.", eX: "The deck could not be opened right now — please try again in a minute.",
  noH: "This link is incomplete.", noP: "This deck is prepared for one recipient. Please open the personal link you were sent — or ask your contact at Norwegian AI Summit for it." };
const GATE_NO = { h: "Dette decket er fortrolig.", p: "Skriv inn passordet du har fått for å åpne det.", ph: "Passord", btn: "Åpne", busy: "Åpner…", foot: "Fortrolig · 13. april 2027 · Grieghallen, Bergen",
  e429: "For mange forsøk. Prøv igjen om 15 minutter.", e401: "Passordet stemmer ikke.", eX: "Decket kunne ikke åpnes akkurat nå — prøv igjen om et minutt.",
  noH: "Lenken er ufullstendig.", noP: "Dette decket er utarbeidet for én mottaker. Åpne den personlige lenken du fikk — eller be kontakten din i Norwegian AI Summit om den." };

export function DeckGate({ deck, title, requireRecipient = false, no = false, children }) {
  const T = no ? GATE_NO : GATE_EN;
  const [sp] = useSearchParams();
  const token = (sp.get("r") || "").trim();
  const [state, setState] = useState("checking");
  const [rcp, setRcp] = useState(token ? null : { mottaker: "", kategori: "", token: "" });
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const params = token ? { r: token } : {};
  useEffect(() => {
    if (!token) return;
    api.get(`/deck/${deck}/r/${encodeURIComponent(token)}`)
      .then(({ data }) => setRcp({ mottaker: data.mottaker || "", kategori: data.kategori || "", token }))
      .catch(() => setRcp({ mottaker: "", kategori: "", token: "" }));
  }, [deck, token]);
  useEffect(() => {
    api.get(`/deck/${deck}/status`, { params }).then(() => setState("open")).catch(() => setState("locked"));
  }, [deck]); // eslint-disable-line react-hooks/exhaustive-deps
  const send = async (e) => {
    e.preventDefault();
    if (!pw.trim() || busy) return;
    setBusy(true);
    setErr("");
    try {
      await api.post(`/deck/${deck}/unlock`, { password: pw });
      api.get(`/deck/${deck}/status`, { params }).catch(() => {});
      setState("open");
    } catch (ex) {
      const st = ex?.response?.status;
      setErr(st === 429 ? T.e429 : st === 401 ? T.e401 : T.eX);
    } finally {
      setBusy(false);
    }
  };
  if (state === "open" && rcp && requireRecipient && !rcp.mottaker) {
    return (
      <div className="hd-gate" data-testid="hd-gate-norecipient">
        <div className="hd-gate-card">
          <span className="hd-nav"><Brand /><span className="d">{title}</span></span>
          <h1 className="hd-gate-h">{T.noH}</h1>
          <p className="hd-gate-p">{T.noP}</p>
          <p className="hd-gate-foot">{T.foot}</p>
        </div>
      </div>
    );
  }
  if (state === "open" && rcp) return <RecipientCtx.Provider value={rcp}>{children}</RecipientCtx.Provider>;
  if (state === "checking" || (state === "open" && !rcp)) return <div className="hd-gate" data-testid="hd-gate-checking" />;
  return (
    <div className="hd-gate" data-testid="hd-gate">
      <form className="hd-gate-card" onSubmit={send}>
        <span className="hd-nav">
          <Brand />
          <span className="d">{title}</span>
        </span>
        <h1 className="hd-gate-h">{T.h}</h1>
        <p className="hd-gate-p">{T.p}</p>
        <label className="hd-gate-field">
          <input type="password" name="deck-access-code" value={pw} onChange={(e) => setPw(e.target.value)} placeholder={T.ph} autoFocus autoComplete="off" data-testid="hd-gate-input" />
          <button type="submit" disabled={busy || !pw.trim()} data-testid="hd-gate-btn">{busy ? T.busy : T.btn}</button>
        </label>
        {err && <p className="hd-gate-err" role="alert" data-testid="hd-gate-error">{err}</p>}
        <p className="hd-gate-foot">{T.foot}</p>
      </form>
    </div>
  );
}
